/**
 * Feature Flag Definitions
 * 
 * All available feature flags must be defined here with their metadata and default values.
 * The `key` field MUST match the Firestore document ID in `/featureFlags/{key}`.
 * 
 * When removing a flag from this definition, delete the corresponding Firestore documents:
 * - /featureFlags/{key}
 * - /orgs/{orgUid}/featureFlags/{key} for all orgs
 */

/**
 * Feature flag keys
 * 
 * Use `as const` pattern for type safety and consistency with other constants in this codebase.
 * This provides both runtime values and compile-time types without enum overhead.
 */
export const FEATURE_FLAG_KEYS = {
  COMMUNICATIONS_SYSTEM: "communicationsSystem",
  CMS_ENABLED: "cmsEnabled",
} as const;

/**
 * Type representing a valid feature flag key
 * Derived from FEATURE_FLAG_KEYS for type safety
 */
export type FeatureFlagKey = typeof FEATURE_FLAG_KEYS[keyof typeof FEATURE_FLAG_KEYS];

export interface FeatureFlagDefinition {
  key: FeatureFlagKey;
  defaultValue: boolean | string | number | object;
  description: string;
  metadata?: {
    rolloutPercentage?: number;
    category?: string;
  };
}

export const FEATURE_FLAGS: Record<FeatureFlagKey, FeatureFlagDefinition> = {
  [FEATURE_FLAG_KEYS.COMMUNICATIONS_SYSTEM]: {
    key: FEATURE_FLAG_KEYS.COMMUNICATIONS_SYSTEM,
    defaultValue: true,
    description: "Enable the communications system",
  },
  [FEATURE_FLAG_KEYS.CMS_ENABLED]: {
    key: FEATURE_FLAG_KEYS.CMS_ENABLED,
    defaultValue: true,
    description: "Enable the content management system for database-driven pages",
  },
};

/**
 * Get a feature flag definition by key
 */
export function getFeatureFlagDefinition(
  key: FeatureFlagKey,
): FeatureFlagDefinition | undefined {
  return FEATURE_FLAGS[key];
}

/**
 * Get all feature flag keys
 */
export function getAllFeatureFlagKeys(): FeatureFlagKey[] {
  return Object.keys(FEATURE_FLAGS) as FeatureFlagKey[];
}

/**
 * Check if a feature flag key is valid (exists in definitions)
 */
export function isValidFeatureFlagKey(key: string): key is FeatureFlagKey {
  return key in FEATURE_FLAGS;
}
